/**
 * The locale list for the public site — the ONE place a language is added.
 *
 * Everything else derives from this list: the proxy's redirect targets,
 * generateStaticParams, the language switcher, hreflang alternates, the
 * sitemap and the message-completeness check. To launch a language, set
 * `enabled: true` here and add `messages/<code>.json` — see messages/README.md.
 *
 * Codes are bare BCP-47 language subtags. Regional variants fold into them at
 * detection time (es-MX → es, pt-BR → pt); see detect.ts. This file must stay
 * free of Next/React imports so the proxy, the tests and the check script can
 * all load it.
 */

export type TextDirection = 'ltr' | 'rtl';

export interface LocaleDefinition {
  /** URL prefix and messages file name — a lower-case language subtag. */
  code: string;
  /** The language's name in itself; what the switcher shows. */
  label: string;
  /** Writing direction, applied to <html dir>. */
  dir: TextDirection;
  /** Open Graph `og:locale` value, in its ll_CC form. */
  ogLocale: string;
  /** Routed, pre-rendered, listed in the switcher and the sitemap. */
  enabled: boolean;
}

export const LOCALE_DEFINITIONS = [
  { code: 'en', label: 'English', dir: 'ltr', ogLocale: 'en_US', enabled: true },
  { code: 'es', label: 'Español', dir: 'ltr', ogLocale: 'es_ES', enabled: true },
  { code: 'pt', label: 'Português', dir: 'ltr', ogLocale: 'pt_BR', enabled: true },
  { code: 'de', label: 'Deutsch', dir: 'ltr', ogLocale: 'de_DE', enabled: true },
  { code: 'fr', label: 'Français', dir: 'ltr', ogLocale: 'fr_FR', enabled: true },
  // To add another language: one entry here plus messages/<code>.json.
  // Nothing per page or per component. To pause one: enabled: false.
] as const satisfies readonly LocaleDefinition[];

export type AppLocale = (typeof LOCALE_DEFINITIONS)[number]['code'];

/** The source-of-truth language: the only complete catalogue, and the fallback. */
export const DEFAULT_LOCALE = 'en' satisfies AppLocale;

/** Enabled locale codes, in switcher order. */
export const LOCALES: readonly AppLocale[] = LOCALE_DEFINITIONS.filter((d) => d.enabled).map(
  (d) => d.code,
);

export function isLocale(value: unknown): value is AppLocale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

export function localeDefinition(code: AppLocale): LocaleDefinition {
  return LOCALE_DEFINITIONS.find((d) => d.code === code) ?? LOCALE_DEFINITIONS[0];
}

/**
 * Written ONLY by the language switcher, never by detection — so a direct
 * link to /es/… can never change what a visitor sees on their next visit.
 */
export const LOCALE_COOKIE = 'NEXT_LOCALE';
export const LOCALE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/** A route param as a locale: enabled values pass through, anything else is English. */
export function toLocale(value: unknown): AppLocale {
  return isLocale(value) ? value : DEFAULT_LOCALE;
}
